import R from "react"
import { useNavigate } from "react-router-dom"
import * as P from "../program-state"
import * as C from "../navigation"
import * as Controls from "../controls"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons"

type plan_card_props = {
    plan : P.usage_tier_t
    is_popular : boolean
    is_current? : boolean
}

const Plan_card = ({plan, is_popular, is_current = false} : plan_card_props) => {
    const [ps, us] = P.use_authenticated_program_state()
    const [subscribing, set_subscribing] = R.useState(false)

    const subscribe_plan = async () => {
        set_subscribing(true)
        const [ok, resp] = await P.create_checkout_session(us, plan.stripe_price_id)
        if (ok) {
            window.location.href = resp.uri
        } else {
            set_subscribing(false)
            alert("Failed to create checkout session. Please try again.")
        }
    }

    const campaign_text = plan.campaign_limit
        ? `Up to ${plan.campaign_limit} campaigns`
        : 'Unlimited campaigns'

    const brand_text = plan.brand_limit
        ? `${plan.brand_limit} brand${plan.brand_limit > 1 ? 's' : ''}`
        : 'Unlimited brands'

    const team_text = plan.team_seats === 1
        ? '1 user'
        : `Up to ${plan.team_seats} users`

    return (
        <Controls.Eres_card className={`tw-relative tw-h-full ${is_popular || is_current ? 'tw-border-2 tw-border-big-stone-rlydark' : ''}`}>
            {is_current && (
                <div className="tw-absolute tw-top-3 tw-right-3 tw-bg-green-600 tw-text-white tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold">
                    Current Plan
                </div>
            )}
            {is_popular && !is_current && (
                <div className="tw-absolute tw-top-3 tw-right-3 tw-bg-big-stone-rlydark tw-text-white tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-font-semibold">
                    Most Popular
                </div>
            )}

            <div className="tw-p-6">
                <div className="tw-mb-6">
                    <h2 className="tw-text-2xl tw-font-bold tw-text-big-stone-900">{plan.name}</h2>
                    <div className="tw-mt-3">
                        <span className="tw-text-4xl tw-font-bold tw-text-big-stone-rlydark">${plan.price}</span>
                        <span className="tw-text-gray-600">/month</span>
                    </div>
                    <p className="tw-text-sm tw-text-gray-600 tw-mt-2">{plan.description}</p>
                </div>

                <div className="tw-space-y-3 tw-mb-6">
                    <div className="tw-flex tw-items-start">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                        <span className="tw-text-sm">{brand_text} included</span>
                    </div>
                    <div className="tw-flex tw-items-start">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                        <span className="tw-text-sm">{campaign_text}</span>
                    </div>
                    <div className="tw-flex tw-items-start">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                        <span className="tw-text-sm">{team_text}</span>
                    </div>
                    <div className="tw-flex tw-items-start">
                        <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                        <span className="tw-text-sm">All social networks</span>
                    </div>
                    {plan.features?.white_label && (
                        <div className="tw-flex tw-items-start">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                            <span className="tw-text-sm">White-label reports</span>
                        </div>
                    )}
                    {plan.features?.automation && (
                        <div className="tw-flex tw-items-start">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                            <span className="tw-text-sm">Automated rules</span>
                        </div>
                    )}
                    {plan.features?.bulk_operations && (
                        <div className="tw-flex tw-items-start">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-mr-3 tw-mt-1" />
                            <span className="tw-text-sm">Bulk operations</span>
                        </div>
                    )}
                </div>

                <Controls.Eres_button
                    className="tw-w-full"
                    onClick={subscribe_plan}
                    disabled={subscribing || is_current}
                >
                    {subscribing ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
                            Processing...
                        </>
                    ) : is_current ? (
                        'Current Plan'
                    ) : (
                        'Subscribe'
                    )}
                </Controls.Eres_button>
            </div>
        </Controls.Eres_card>
    )
}

const Plans_content = () => {
    const [ps, us] = P.use_authenticated_program_state()
    const navigate = useNavigate()
    const [plans, set_plans] = R.useState<P.usage_tier_t[]>([])
    const [subscription, set_subscription] = R.useState<P.user_subscription_t | null>(null)
    const [loading, set_loading] = R.useState(true)
    const [billing_period, set_billing_period] = R.useState<'monthly' | 'annual'>('monthly')

    R.useEffect(() => {
        const fetch_data = async () => {
            const [ok, data] = await P.fetch_all_plans(us)
            const [sub_ok, sub_data] = await P.fetch_user_subscription(us)

            if (ok) set_plans(data)
            if (sub_ok) set_subscription(sub_data)

            set_loading(false)
        }
        fetch_data()
    }, [us])

    if (loading) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
                <FontAwesomeIcon icon={faSpinner} spin className="tw-text-4xl tw-text-blue-600" />
            </div>
        )
    }

    // filter plans by billing period
    const filtered_plans = plans.filter(p => {
        if (billing_period === 'monthly') {
            return p.eligibility_duration === 1
        } else {
            return p.eligibility_duration === 12
        }
    })

    // get tier names and find corresponding plans
    const tier_names = ['Starter', 'Growth', 'Professional']
    const display_plans = tier_names
        .map(name => filtered_plans.find(p => p.name.includes(name)))
        .filter((p): p is P.usage_tier_t => p !== undefined)

    const get_popular_plan_index = () => {
        return display_plans.findIndex(p => p.name.includes('Growth'))
    }

    const current_plan_id = subscription?.plan?.id

    // calculate savings for annual plans
    const show_savings = billing_period === 'annual'
    const savings_text = show_savings ? "Save 2 months!" : null

    return (
        <div className="tw-space-y-6">
            <div className="tw-text-center tw-mb-8">
                <h1 className="tw-text-3xl tw-font-bold tw-mb-2">Brand-Based Pricing</h1>
                <p className="tw-text-gray-600">
                    Choose the plan that fits your business needs
                </p>
            </div>

            {/* billing period toggle */}
            <div className="tw-flex tw-justify-center tw-mb-8">
                <div className="tw-relative tw-bg-gray-200 tw-p-1 tw-rounded-lg tw-flex tw-gap-1">
                    <button
                        onClick={() => set_billing_period('monthly')}
                        className={`
                            tw-px-6 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all
                            ${billing_period === 'monthly'
                                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                : 'tw-text-gray-700 hover:tw-text-gray-900'
                            }
                        `}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => set_billing_period('annual')}
                        className={`
                            tw-px-6 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-transition-all
                            ${billing_period === 'annual'
                                ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                                : 'tw-text-gray-700 hover:tw-text-gray-900'
                            }
                        `}
                    >
                        Annual
                        {savings_text && (
                            <span className="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-full tw-bg-green-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-800">
                                {savings_text}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
                {display_plans.map((plan, index) => {
                    const is_current = plan.id === current_plan_id
                    const is_popular = index === get_popular_plan_index()

                    return (
                        <Plan_card
                            key={plan.id}
                            plan={plan}
                            is_popular={is_popular && !is_current}
                            is_current={is_current}
                        />
                    )
                })}
            </div>

            <Controls.Eres_card>
                <div className="tw-p-6">
                    <h2 className="tw-text-2xl tw-font-bold tw-mb-4 tw-text-center">All Plans Include:</h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
                        <div className="tw-text-center">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-text-2xl tw-mb-2" />
                            <div className="tw-font-semibold">All Social Networks</div>
                            <div className="tw-text-sm tw-text-gray-600">
                                Facebook, Instagram, LinkedIn, TikTok
                            </div>
                        </div>
                        <div className="tw-text-center">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-text-2xl tw-mb-2" />
                            <div className="tw-font-semibold">Smart Campaigns</div>
                            <div className="tw-text-sm tw-text-gray-600">
                                Cross-platform campaign management
                            </div>
                        </div>
                        <div className="tw-text-center">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-text-2xl tw-mb-2" />
                            <div className="tw-font-semibold">Unified Dashboard</div>
                            <div className="tw-text-sm tw-text-gray-600">
                                All metrics in one place
                            </div>
                        </div>
                        <div className="tw-text-center">
                            <FontAwesomeIcon icon={faCheck} className="tw-text-green-600 tw-text-2xl tw-mb-2" />
                            <div className="tw-font-semibold">CSV Export</div>
                            <div className="tw-text-sm tw-text-gray-600">
                                Download your data anytime
                            </div>
                        </div>
                    </div>
                </div>
            </Controls.Eres_card>

            <div className="tw-text-center tw-text-gray-600 tw-mt-8">
                <p>
                    Need more than 15 brands?{' '}
                    <a href="/account" className="tw-text-big-stone-rlydark tw-underline">
                        Contact us
                    </a>{' '}
                    for Enterprise pricing
                </p>
            </div>
        </div>
    )
}

export default () => {
    return <C.Emit_main page_id="PAGE-plans"><Plans_content /></C.Emit_main>
}
